import { createSelector } from 'reselect';
import { List } from 'immutable';
import { STATE_NAME } from './constants';
import { getMarketName } from '../app/selectors';
import { MARKET_GLOBAL } from '../app/constants';
import {
  getPpiById,
  hasPublishedEnrichmentData as ppiHasPublishedEnrichmentData,
  hasInProgressEnrichmentData as ppiHasInProgressEnrichmentData
} from '../PpiContainer/selectors';

export const getStateSlice = state => state.get(STATE_NAME);

export const getPpsiMap = state => getStateSlice(state).get('ppsis');

export const getReviewPpsiMap = state =>
  getStateSlice(state).get('reviewPpsis');

export const getPpsiReviewMap = state =>
  getStateSlice(state).get('ppsisReviewSummary');

export const getTotalRangePpsiMap = state =>
  getStateSlice(state).get('totalRangeReport');

export const getPublishedUnpublishedPpsis = state =>
  getStateSlice(state).get('publishedUnpublishedPpsis');

export const getIsFetchingCopyLocal = state =>
  getStateSlice(state).get('isFetchingCopyLocal');

export const getShow = state => getStateSlice(state).get('show');

export const getGlobalApprovalPpsi = state =>
  getStateSlice(state).get('globalApprovalPpsis');

export const getGlobalStatusPpsisMap = state =>
  getStateSlice(state).get('globalStatusPpsis');

export const totalEngagementReportMarketList = state => {
  return getStateSlice(state).get('totalEngagementReportMarketList');
};
export const totalInteractionReport = state => {
  return getStateSlice(state).get('totalEngagementReport');
};
export const getIsFetchingInteractionReport = state => {
  return getStateSlice(state).get('isFetchingEngagementReport');
};
export const getIsFetchingEngagementReportMarketList = state => {
  return getStateSlice(state).get('isFetchingEngagementReportMarketList');
};

// NIR Market
export const getGlobalPublishedPpsiNirList = state =>
  getStateSlice(state).get('globalPublishedPpsisNir');

// global Ppi local Market
export const getPiaProductAreasNir = state =>
  getStateSlice(state).get('piaProductAreas');

//global Ppi local Market
export const getGlobalPublishedPpiIfsList = state =>
  getStateSlice(state).get('globalPublishedPpiIfs');

// Market Dish Logo
export const getMarketDishLogo = state =>
  getStateSlice(state).get('marketDishLogo');

export const getPpsis = createSelector(
  getPpsiMap,
  ppsiMap => ppsiMap.toList()
);

export const getReviewPpsis = createSelector(
  getReviewPpsiMap,
  ppsiMap => ppsiMap.toList()
);

export const getTotalRangeReport = createSelector(
  getTotalRangePpsiMap,
  ppsiMap => ppsiMap.toList()
);

export const getvaraintPpsisMap = state =>
  getStateSlice(state).get('varaintPpsis');

export const getvaraintPpsis = createSelector(
  getvaraintPpsisMap,
  ppsiMap => ppsiMap.toJS()
);

export const getGlobalStatusPpsis = createSelector(
  getGlobalStatusPpsisMap,
  ppsiMap => ppsiMap.toList()
);

export const getPpsisReviewSummary = createSelector(
  getPpsiReviewMap,
  ppsiMap => ppsiMap.toList()
);

// export const getGlobalApprovalPpsi = createSelector(
//   getGlobalApprovalPpsiMap,
//   ppsiMap => ppsiMap.toList()
// );

function filterReviewPpsisByStatus(ppsis, byStatus) {
  if (!byStatus) {
    return ppsis;
  }
  return ppsis.filter(ppsi => {
    const hasInProgress = ppsi.get('inProgressPpsiEnrichmentData');
    const matchesInProgress =
      hasInProgress &&
      ppsi.get('inProgressPpsiEnrichmentData').get('status') === byStatus;

    return matchesInProgress;
  });
}

const FilterPredicate = {
  market: market => ppsi => ppsi.get('market') === market,

  /**
   * Get PPSIs with a specific status. Either the inProgress or published data
   * entity needs to match the status.
   *
   * @param status Status to filter by.
   * @returns {Function}
   */
  status: status => ppsi => {
    const hasInProgress = ppsi.get('inProgressPpsiEnrichmentData');
    const matchesInProgress =
      hasInProgress &&
      ppsi.get('inProgressPpsiEnrichmentData').get('status') === status;

    const hasPublished = ppsi.get('publishedPpsiEnrichmentData');
    const matchesPublished =
      hasPublished &&
      ppsi.get('publishedPpsiEnrichmentData').get('status') === status;

    return matchesInProgress || matchesPublished;
  },

  /**
   * Get PPSIs which uses PPI. ONLY when the PPI is used in the published PPSIs
   * it is included in the result.
   *
   * @param ppiEnrichmentId
   * @returns {Function}
   */
  relatedToPpi: ppiEnrichmentId => ppsi => {
    const publishedPpsiData = ppsi.get('publishedPpsiEnrichmentData');
    if (publishedPpsiData) {
      const ppiDetails = publishedPpsiData.get('ppiDetails');
      if (ppiDetails) {
        if (
          ppiDetails.some(
            ppiDetail =>
              ppiDetail.get('ppiEnrichmentDataId') === ppiEnrichmentId
          )
        ) {
          return true;
        }
      }
    }
    const inProgressPpsiData = ppsi.get('inProgressPpsiEnrichmentData');
    if (inProgressPpsiData) {
      const ppiDetails = inProgressPpsiData.get('ppiDetails');
      if (ppiDetails) {
        if (
          ppiDetails.some(
            ppiDetail =>
              ppiDetail.get('ppiEnrichmentDataId') === ppiEnrichmentId
          )
        ) {
          return true;
        }
      }
    }
    return false;
  },

  notUpToDate: () => ppsi => {
    const status = ppsi.get('status');
    if (status === 'PUBLISHED' || status === 'UNPUBLISHED') {
      const publishedPpsiData = ppsi.get('publishedPpsiEnrichmentData');
      if (publishedPpsiData && publishedPpsiData.get('upToDate') === false) {
        return true;
      }
      const inProgressPpsiData = ppsi.get('inProgressPpsiEnrichmentData');
      if (inProgressPpsiData && publishedPpsiData.get('upToDate') === false) {
        return true;
      }
    } else {
      const inProgressPpsiData = ppsi.get('inProgressPpsiEnrichmentData');
      if (inProgressPpsiData && inProgressPpsiData.get('upToDate') === false) {
        return true;
      }
    }
    return false;
  }
};

export const getPpsisWithFilter = ({
  status,
  // Related to PPI. Included only if PPSI uses the published PPI.
  relatedToPpiId,
  matchingUserMarket = true,
  matchingNotUptoDate = false
}) =>
  createSelector(
    getPpsis,
    getMarketName,
    getPpiById(relatedToPpiId),
    ppiHasPublishedEnrichmentData(relatedToPpiId),
    ppiHasInProgressEnrichmentData(relatedToPpiId),
    (
      ppsis,
      market,
      relatedToPpi,
      ppiHasPublishedData,
      ppiHasInprogressData
    ) => {
      let filteredPpsis = ppsis;
      if (matchingUserMarket) {
        filteredPpsis = filteredPpsis.filter(FilterPredicate.market(market));
      }
      if (status) {
        filteredPpsis = filteredPpsis.filter(FilterPredicate.status(status));
      }
      if (relatedToPpiId) {
        if (ppiHasPublishedData && ppiHasInprogressData) {
          const publishedPPIEnrichmentId = relatedToPpi
            .get('publishedPPIEnrichmentData')
            .get('id');
          const inProgressPPIEnrichmentId = relatedToPpi
            .get('inProgressPPIEnrichmentData')
            .get('id');
          filteredPpsis = filteredPpsis.filter(
            FilterPredicate.relatedToPpi(publishedPPIEnrichmentId) ||
              FilterPredicate.relatedToPpi(inProgressPPIEnrichmentId)
          );
        } else if (ppiHasPublishedData) {
          const publishedPPIEnrichmentId = relatedToPpi
            .get('publishedPPIEnrichmentData')
            .get('id');
          filteredPpsis = filteredPpsis.filter(
            FilterPredicate.relatedToPpi(publishedPPIEnrichmentId)
          );
        } else if (ppiHasInprogressData) {
          const inProgressPPIEnrichmentId = relatedToPpi
            .get('inProgressPPIEnrichmentData')
            .get('id');
          filteredPpsis = filteredPpsis.filter(
            FilterPredicate.relatedToPpi(inProgressPPIEnrichmentId)
          );
        } else {
          return new List();
        }
      }
      if (matchingNotUptoDate) {
        filteredPpsis = filteredPpsis.filter(FilterPredicate.notUpToDate());
      }
      return filteredPpsis;
    }
  );

/**
 * Get global PPSIs that are applicable for current users market.
 *
 * This method is only usable for non-IFS user.
 */
export const getGlobalPpsis = createSelector(
  getPpsis,
  ppsis => ppsis.filter(ppi => ppi.get('market') === MARKET_GLOBAL)
);

export const getReviewPpsisWithFilter = createSelector(
  getPpsis,
  ppsis => ppsis.filter(ppi => ppi.get('market') === MARKET_GLOBAL)
);

export const hasPublishedEnrichmentData = id =>
  createSelector(
    getPpsiMap,
    ppsiMap => {
      if (!ppsiMap.has(id)) {
        return false;
      }
      const ppsi = ppsiMap.get(id);
      return !!ppsi.get('publishedPpsiEnrichmentData');
    }
  );

export const getPpsiById = id =>
  createSelector(
    getPpsiMap,
    ppsiMap => ppsiMap.get(id)
  );

export const getMarketPpsiById = id =>
  createSelector(
    getReviewPpsiMap,
    ppsiMap => ppsiMap.get(id)
  );

export const getPpsiByEnrichmentId = enrichmentId =>
  createSelector(
    getPpsis,
    ppsis => {
      if (!enrichmentId) {
        return List();
      }
      return ppsis
        .filter(ppsi => {
          const inProgressId = ppsi.getIn([
            'inProgressPpsiEnrichmentData',
            'id'
          ]);
          const publishedId = ppsi.getIn(['publishedPpsiEnrichmentData', 'id']);
          return inProgressId === enrichmentId || publishedId === enrichmentId;
        })
        .first();
    }
  );

export const getUpdatePpsis = state => getStateSlice(state).get('updatePpsis');

export const getGlobalUpdatePpsis = state =>
  getStateSlice(state).get('updateGlobalPpsis');
// export const getReviewPpsisWithStatus = (withStatus, marketCode) =>
// createSelector(
//   getPpsis,
//   ppsis => filterReviewPpsisByStatus(ppsis, withStatus)
// );

export const getIsFetching = state => getStateSlice(state).get('isFetching');

export const getIsFetchingDishLogo = state =>
  getStateSlice(state).get('isFetchingDishLogo');

export const getIsInDraftFetched = state =>
  getStateSlice(state).get('isInDraftFetched');

export const getFetchError = state => getStateSlice(state).get('fetchError');

export const getFetchvaraintError = state =>
  getStateSlice(state).get('varaintPpsisError');
export const getIsFetchingVariant = state =>
  getStateSlice(state).get('isFetchingVariant');

export const getHasRecalledError = state =>
  getStateSlice(state).get('hasRecalledError');

export const getReviewMarketCount = state =>
  getStateSlice(state).get('reviewMarketCount');

export const getRecalledStatus = state =>
  getStateSlice(state).get('recalledStatus');

export const getIsSaving = state => getStateSlice(state).get('isSaving');

export const getLastSavedPpsiId = state =>
  getStateSlice(state).get('lastSavedPpsiId');

export const getIsApproving = state => getStateSlice(state).get('isApproving');

export const getApproveError = state =>
  getStateSlice(state).get('approveError');

export const getIsPublishing = state =>
  getStateSlice(state).get('isPublishing');

export const getPublishError = state =>
  getStateSlice(state).get('publishError');

export const getIsUnpublishing = state =>
  getStateSlice(state).get('isUnpublishing');

export const getUnpublishError = state =>
  getStateSlice(state).get('unpublishError');

export const getIsDiscardingDraft = state =>
  getStateSlice(state).get('isdiscardingDraft');

export const getDicardDraftError = state =>
  getStateSlice(state).get('discardDraftError');

export const getIsMarkingAsUptoDate = state =>
  getStateSlice(state).get('isMarkingAsUptoDate');

export const getMarkAsUptoDateError = state =>
  getStateSlice(state).get('markAsUptoDateError');

export const getIsCreatingDraft = state =>
  getStateSlice(state).get('isCreatingDraft');

export const getCreateDraftError = state =>
  getStateSlice(state).get('createDraftError');

export const isFetchingSummary = state =>
  getStateSlice(state).get('isFetchingReviewSummary');

export const getIsSendingForReview = state =>
  getStateSlice(state).get('isSendingForReview');

export const getSendReviewError = state =>
  getStateSlice(state).get('sendReviewError');

export const getIsglobalApproving = state =>
  getStateSlice(state).get('isglobalApproving');

export const getGlobalApproveError = state =>
  getStateSlice(state).get('globalApproveError');

export const getIsglobalRejecting = state =>
  getStateSlice(state).get('isglobalRejecting');

export const getGlobalRejectError = state =>
  getStateSlice(state).get('globalRejectError');

export const getIsFetchingGlobalStatus = state =>
  getStateSlice(state).get('isFetchingGlobalStatus');

export const getGlobalStatusError = state =>
  getStateSlice(state).get('globalStatusError');

// ppsi smart update - IFPPSI-526

export const getIsImportingGlobalUpdate = state =>
  getStateSlice(state).get('isImportingGlobalUpdate');

export const getimportingGlobalUpdateError = state =>
  getStateSlice(state).get('importingGlobalUpdateError');

// Total range dish statistics changes

export const getIsFetchingRangeReport = state =>
  getStateSlice(state).get('isFetchingRangeReport');

export const getRangeReportError = state =>
  getStateSlice(state).get('rangeReportError');

export const getTotalInteractionReport = createSelector(
  totalInteractionReport,
  ppsiMap => ppsiMap.toList()
);

export const getInteractionMarketListReport = createSelector(
  totalEngagementReportMarketList,
  ppsiMap => ppsiMap.toList()
);

// For uploading BOM Reports to NIR markets
export const getIsBomReportUploading = state =>
  getStateSlice(state).get('isBomReportUploading');

export const getBomReportUploadError = state =>
  getStateSlice(state).get('bomReportUploadError');

export const getBomReportUploadSuccess = state =>
  getStateSlice(state).get('bomReportUploadSuccess');

export const getBomReportUploadReset = state =>
  getStateSlice(state).get('bomReportUploadReset');

// For getting Global non-market specific PPSIS for NIR
export const getGlobalPublishedPppsisNir = createSelector(
  getGlobalPublishedPpsiNirList,
  ppsiMap => ppsiMap.toList()
);

// Fetch global PPi IFS Market for local market
export const getGlobalPublishedPpiIfs = createSelector(
  getGlobalPublishedPpiIfsList,
  ppsiMap => ppsiMap.toList()
);

export const getPpsiId = state => getStateSlice(state).get('ppsiId');
export const getEnableAvailablity = state =>
  getStateSlice(state).get('isEnableAvailablity');
